<template>
    <ComboboxRoot
        v-slot="{ open }"
        v-model:open="isOpen"
        v-model="model"
        :disabled="disabled"
        :reset-search-term-on-blur="false"
        :class="['rounded-lg', disabled ? 'bg-control-disabled-bg' : 'bg-control-bg']"
        @highlight="onHighlight">
        <ComboboxAnchor
            class="border border-control-border w-full inline-flex justify-between items-center rounded-lg pr-4"
            :class="[isInputFocused && 'ring-2 ring-control-ring', disabled && 'bg-control-disabled-bg']">
            <ComboboxInput
                :id="inputId"
                v-model="inputModel"
                :auto-focus="autoFocus"
                :disabled="disabled"
                :placeholder="activePlaceholder"
                :display-value="inputDisplayValue"
                class="w-full outline-hidden rounded-lg body-small font-normal p-2 disabled:bg-control-disabled-bg comboboxInput"
                @blur="isInputFocused = false"
                @focus="isInputFocused = true" />
            <ComboboxCancel v-if="clearable" class="mr-2 flex items-center comboboxCancel" @click="handleCancel">
                <i v-if="inputModel?.length > 0" class="bi bi-x-lg leading-none" aria-hidden="true" />
            </ComboboxCancel>
            <ComboboxTrigger :class="['flex items-center comboboxTrigger', { invisible: disabled }]">
                <slot name="icon">
                    <i class="bi leading-none" :class="[open ? 'bi-chevron-up' : 'bi-chevron-down']" aria-hidden="true"></i>
                </slot>
            </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxPortal :disabled="disablePortal">
            <ComboboxContent
                :side-offset="sideOffset"
                position="popper"
                class="w-(--reka-combobox-trigger-width) bg-control-bg overflow-auto rounded-lg shadow-md border comboboxContent z-40">
                <ComboboxViewport ref="viewport" class="max-h-100 comboboxViewport">
                    <slot name="empty">
                        <ComboboxEmpty class="p-2 body-small font-normal" />
                    </slot>
                    <!-- Single Items -->
                    <ComboboxItem
                        v-for="option in options"
                        :key="keyValue(option)?.toString()"
                        ref="combobox-item"
                        :value="option ?? {}"
                        :text-value="displayValue(option)?.toString() ?? ''"
                        class="cursor-pointer p-2 m-1 rounded-lg body-small flex items-center comboboxItem data-highlighted:bg-combobox-option-hover hover:bg-combobox-option-hover">
                        <span class="grow">
                            <slot name="item" :option="option">
                                {{ displayValue(option) }}
                            </slot>
                        </span>
                        <ComboboxItemIndicator class="bi bi-check text-xl/none comboboxItemIndicator" />
                    </ComboboxItem>

                    <ComboboxSeparator v-if="options.length > 0 && actionRows.length > 0" class="mx-2 my-1 h-px bg-combobox-separator comboboxSeparator" />

                    <ComboboxItem
                        v-for="row in actionRows"
                        :key="`${row.action.id}:${inputModel}`"
                        :value="row.value"
                        :text-value="inputModel"
                        class="cursor-pointer p-2 m-1 rounded-lg body-small-semibold flex items-center comboboxAction data-highlighted:bg-combobox-option-hover hover:bg-combobox-option-hover"
                        @select.prevent="onActionSelect(row.action)">
                        <slot name="action" :action="row.action">
                            {{ row.action.label }}
                        </slot>
                    </ComboboxItem>

                    <div role="status" aria-live="polite">
                        <div v-if="isLoadingMore" class="flex items-center justify-center gap-2 p-2 body-small">
                            <slot name="loading-more">Loading more...</slot>
                        </div>
                    </div>
                </ComboboxViewport>
            </ComboboxContent>
        </ComboboxPortal>
    </ComboboxRoot>
</template>

<script lang="ts">
export type ComboboxAction = {
    id: string;
    label: string;
    closeOnSelect?: boolean;
};
</script>

<script setup lang="ts" generic="T extends AcceptableValue">
import { unrefElement, useInfiniteScroll } from "@vueuse/core";
import {
    type AcceptableValue,
    ComboboxAnchor,
    ComboboxCancel,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxItemIndicator,
    ComboboxPortal,
    ComboboxRoot,
    ComboboxSeparator,
    ComboboxTrigger,
    ComboboxViewport,
} from "reka-ui";
import { computed, nextTick, onMounted, ref, useTemplateRef } from "vue";

type BaseComboboxProps<T> = {
    options: T[];
    displayValue?: (option: T) => AcceptableValue;
    keyValue?: (option: T) => PropertyKey;
    placeholder?: string;
    sideOffset?: number;
    disablePortal?: boolean;
    inputId?: string;
    disabled?: boolean;
    autoOpen?: boolean;
    autoFocus?: boolean;
    actions?: ComboboxAction[];
    hasMore?: boolean;
    isLoadingMore?: boolean;
    /** Disable for required fields that have no valid empty state. */
    clearable?: boolean;
};

const model = defineModel<T | null>();

const {
    placeholder = "Search an option",
    // @ts-expect-error - generic typing not working as expected here
    displayValue = (option: T) => option.label,
    // @ts-expect-error - generic typing not working as expected here
    keyValue = (option: T) => option.value,
    sideOffset = 5,
    // used for testing purposes where portaling has issues
    disablePortal = false,
    disabled = false,
    autoOpen = false,
    autoFocus = false,
    actions = [],
    hasMore = false,
    clearable = true,
} = defineProps<BaseComboboxProps<T>>();

const emit = defineEmits<{ action: [id: string]; "load-more": [] }>();

// The scrollable element lives inside this component, so paging on scroll can only be detected here.
const viewport = useTemplateRef("viewport");
useInfiniteScroll(
    () => unrefElement(viewport),
    () => emit("load-more"),
    { distance: 80, canLoadMore: () => hasMore },
);

const isOpen = ref(false);
const highlighted = ref<string | null>(null);
const activePlaceholder = computed(() => {
    if (isOpen.value) {
        return highlighted.value ?? placeholder;
    }
    return placeholder;
});

const inputModel = defineModel<string>("input", { default: "" });
onMounted(() => {
    nextTick(() => {
        // `disabled` does not stop reka opening the popover — it only governs what happens once open —
        // so auto-open has to check it here, or a disabled combobox pops its list open on mount.
        isOpen.value = autoOpen && !!inputModel.value && !disabled;
    });
});
const isInputFocused = ref(false);

// this any typing comes from the library itself
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const inputDisplayValue = (val: any) => {
    return val ? (displayValue(val as T)?.toString() ?? "") : inputModel.value;
};

const handleCancel = () => {
    // reka's ComboboxCancel has no disabled handling of its own, so without this the clear button still
    // wipes the model on a disabled combobox.
    if (disabled) {
        return;
    }
    inputModel.value = "";
    model.value = null;
};

// ComboboxItem requires a non-empty `value`, but an action is not a value. Each action gets an opaque
// sentinel that stays private to this component: `@select.prevent` means it never reaches `model`, so
// consumers never see it and `model` remains typed `T | null`.
type ActionValue = { __comboboxAction: string };

const isActionValue = (value: unknown): value is ActionValue => typeof value === "object" && value !== null && "__comboboxAction" in value;

const actionRows = computed(() => actions.map((action) => ({ action, value: { __comboboxAction: action.id } as ActionValue })));

const onActionSelect = (action: ComboboxAction) => {
    // reka's ListboxItem emits `select` before it checks `disabled`, so forwarding `disabled` to
    // ComboboxRoot does not stop this handler running. The guard is load-bearing.
    if (disabled) {
        return;
    }
    if (action.closeOnSelect ?? true) {
        isOpen.value = false;
    }
    emit("action", action.id);
};

const onHighlight = (payload: { ref: HTMLElement; value: AcceptableValue } | undefined) => {
    const value = payload?.value;
    if (!value) {
        return;
    }
    if (isActionValue(value)) {
        // `displayValue` expects a `T`; read the action's own label instead of forcing it through.
        highlighted.value = actions.find((action) => action.id === value.__comboboxAction)?.label ?? null;
        return;
    }
    highlighted.value = displayValue(value as T)?.toString() ?? null;
};
</script>
